import React, { useState, useEffect } from 'react';
import ReactSVG from 'react-svg';
import { useTranslation } from '~/dependencies';
import { colors } from '~/layout';
import { TimeToRespondIcon } from '~/assets/svgIcons';
import { Chip } from './styles';

type TTRCounterBadgeProps = {
  /**
   * In minutes
   */
  expireIn: number;
};

const TTR_THRESHOLDS = {
  CRITICAL_MINUTES: 60,
  WARNING_MINUTES: 720, // 12 hours
} as const;

const TTR_COLORS = {
  CRITICAL: colors.red09,
  WARNING: colors.yellow04,
  NORMAL: colors.lightBlue13,
  EXPIRED: colors.darkGray12,
} as const;

function formatTimeUnits(minutes: number): string {
  if (minutes < TTR_THRESHOLDS.CRITICAL_MINUTES) {
    return `${minutes}min`;
  }

  const hours = Math.floor(minutes / 60);
  return `${hours}h`;
}

/**
 * Component that displays a badge indicating the time remaining to respond to a conversation.
 * The badge changes color based on the urgency of the response time.
 * If the time has expired, it shows an "Expired" label.
 * If the expireIn prop is 55 minutes, it will show "55min". If it's 120 minutes, it will show "2h".
 * @example label = "55min" if expireIn is 55, label = "2h" if expireIn is 120
 */
function getColorAndLabel(
  expireIn: number,
  expiredLabel: string
): { color: string; label: string } {
  if (expireIn <= 0) {
    return { color: TTR_COLORS.EXPIRED, label: expiredLabel };
  }

  if (expireIn < TTR_THRESHOLDS.CRITICAL_MINUTES) {
    return { color: TTR_COLORS.CRITICAL, label: formatTimeUnits(expireIn) };
  }

  if (expireIn <= TTR_THRESHOLDS.WARNING_MINUTES) {
    return { color: TTR_COLORS.WARNING, label: formatTimeUnits(expireIn) };
  }

  return { color: TTR_COLORS.NORMAL, label: formatTimeUnits(expireIn) };
}

const TTRCounterBadge = React.memo(({ expireIn }: TTRCounterBadgeProps) => {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(expireIn);

  // FIXME: Intentionally using `expireIn` instead of `remaining`. Revert to `remaining` once NWA-3012 is resolved.
  const { color, label } = getColorAndLabel(
    expireIn || remaining,
    t('screens.conversation.ttrBadge.expired')
  );
  const title = `Time to respond: ${label}`;

  useEffect(() => {
    setRemaining(expireIn);
    const interval =
      expireIn > 0
        ? setInterval(() => setRemaining(prev => prev - 1), 60_000)
        : null;

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [expireIn]);

  return (
    <Chip
      name="ttr-counter-badge"
      data-test-id="ttr-badge"
      data-ttr-expired={false}
      color={color}
      title={title}
      label={label}
      avatar={<ReactSVG src={TimeToRespondIcon} />}
    />
  );
});

export default TTRCounterBadge;
